import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, createElementVNode as _createElementVNode, normalizeStyle as _normalizeStyle, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = ["data-device", "data-bp"]
const _hoisted_2 = /*#__PURE__*/_createElementVNode("div", { id: "app-container" }, null, -1)

import { tempTemplateModuleData } from '@/data/tempTemplateModuleData';
import { useDeviceInfoStore } from '@/store/deviceInfo';
import Nested from '@/views/client/Nested.vue';
import { getDateIntervalDuration } from '@amcharts/amcharts5/.internal/core/util/Time';
import axios from 'axios';
import {
  computed,
  onMounted,
  provide,
  reactive,
  ref,
  StyleValue,
  onBeforeMount,
  onBeforeUnmount,
  nextTick,
} from 'vue';
import {
  convertHexToRGBA,
  getApiPathPrefix,
  jsonParsingWithFallback,
} from '@/utils/utils';
import { getCookie } from '@/utils/utils';
import { IsAdmin } from '@/injectionKeys';
import { usePreorderCommonStore } from '@/store/preorderCommon';


import { PhoneSelectOption } from '@/components/modules/common/PreorderCustomSelect.vue';
import { ICoupon } from '@/types/Template';
import { PreorderSubType } from '@/types/PreorderItem';

export interface LoginUserPreorderHistory {
  agreeConsignment: boolean;
  agreeUsage: boolean;
  createDate: string;
  extraData: string;
  gameName: string;
  idx: number;
  phoneNumber?: string;
  email?: string;
  phoneType: number;
  userId: number;
}

export interface IParentData {
  colors: {
    a: string;
    b: string;
    font: string;
  };
  locale: string;
  preorderStartDate: string;
  preorderEndDate: string;
  preorderSubType: PreorderSubType;
  useCertification: boolean;
  usingAreaCode: boolean;
  specificCountryNumber: string;
  usingDropdown: boolean;
  useSelectStore: boolean;
  dropdownOptions: PhoneSelectOption[];
  gameName: string;
  settings: ICoupon;
  phonePattern: RegExp;
  emailPattern: RegExp;
  loginUserInfo?: { phoneNumber: string; email: string } | null;
  loginUserPreorderHistories:
    | Pick<LoginUserPreorderHistory, 'email' | 'phoneNumber' | 'extraData'>[]
    | null;
}

export default /*#__PURE__*/_defineComponent({
  __name: 'Container',
  setup(__props) {

const data = ref<Record<string, any>[]>([]);
const settings = ref();
// [TODO] : 임시파일 삭제
// const gameName = window._gameName ?? 'linwkr-06151030111-copy';
const service = window._service ?? 'linw';

switch (service) {
  case 'linw':
    require('@/fonts/linw_fonts.css');
    break;
  case 'bns2':
    require('@/fonts/bns2_fonts.css');
    break;
  case 'lms':
    require('@/fonts/lms_fonts.css');
    break;
  case 'aion':
    require('@/fonts/aion_fonts.css');
    break;
}

const preorderCommon = usePreorderCommonStore();
const { setParentData } = preorderCommon;

const parentData = reactive<IParentData>({
  colors: {
    a: '',
    b: '',
    font: '',
  },
  locale: 'ko_KR',
  preorderStartDate: '',
  preorderEndDate: '',
  preorderSubType: 'PHONE',
  useCertification: true,
  usingAreaCode: false,
  specificCountryNumber: '',
  usingDropdown: false,
  useSelectStore: false,
  dropdownOptions: [{ value: '', prefix: '', desc: '' }],
  gameName: '',
  settings: {
    coupon: {
      coupons: [],
      displayEndDate: '',
      displayStartDate: '',
      registerStartDate: '',
      registerEndDate: '',
      selectable: false,
      selectableCouponCount: 0,
      type: '',
    },
  },
  phonePattern: /^((010|011|016|017|018|019)[0-9]{7,8})$/g,
  emailPattern: /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/g,
  loginUserInfo: null,
  loginUserPreorderHistories: null,
});
parentData.colors.a = window._colorCode1;
parentData.colors.b = window._colorCode2;
parentData.colors.font = window._colorCode3;
parentData.locale = window._locale || 'ko_KR';
parentData.preorderStartDate = window._preorderStartDate;
parentData.preorderEndDate = window._preorderEndDate;

parentData.preorderSubType = window._preorderSubType;
parentData.useCertification = window._useCertification;
parentData.useSelectStore = window._useSelectStore;
parentData.usingAreaCode = window._usingAreaCode;
parentData.specificCountryNumber = window._specificCountryNumber;
parentData.usingDropdown = window._usingDropdown;
parentData.dropdownOptions = jsonParsingWithFallback(window._dropdownOptions, [
  { value: '', prefix: '', desc: '' },
] as PhoneSelectOption[]);
parentData.phonePattern = new RegExp(window._phonePattern || '');
parentData.emailPattern = new RegExp(window._emailPattern || '');
parentData.gameName = window._gameName;

const parentColor = reactive({
  '--point-color-a': convertHexToRGBA(parentData.colors.a, 1, true),
  '--point-color-b': convertHexToRGBA(parentData.colors.b, 1, true),
  '--point-color-font': convertHexToRGBA(parentData.colors.font, 1, true),
}) as StyleValue;

// provide(IsAdmin, false);
provide('service', service);

const getData = async () => {
  const apiPathPrefix = getApiPathPrefix();

  if (!apiPathPrefix) {
    console.log('apiPathPrefix' + apiPathPrefix);
  }

  // 모듈 데이터 불러오기
  try {
    const result = await axios.get<Record<string, any>>(
      `${apiPathPrefix}/preorder/preOrderRecord/template?gameName=${parentData.gameName}`
    );
    data.value = result.data.modules;
    parentData.settings = result.data.settings;
  } catch {
    console.log('데이터 겟 실패!');
    // [TODO] : 임시파일 삭제
    data.value = tempTemplateModuleData;
  }

  // 로그인한 유저가 사전예약 했을경우 사전예약 정보 가져오기
  try {
    if (window.userData.guid === '') return;

    const result = await axios.get<LoginUserPreorderHistory[]>(
      `${apiPathPrefix}/preorder/preOrderRecord/findByUserId?gameName=${parentData.gameName}`
    );
    const data = result.data;

    if (!data) {
      parentData.loginUserPreorderHistories = null;
      return;
    }

    const loginUserPreorderHistories = data.map((history) => ({
      email: history.email,
      phoneNumber: history.phoneNumber,
      extraData: history.extraData,
    }));

    parentData.loginUserPreorderHistories = loginUserPreorderHistories;
  } catch (err) {
    console.log('유저 데이터 겟 실패');
  }

  // 로그인한 유저인 경우 마이페이지(?) 에 등록한 폰번호/메일주소 가져오기
  try {
    if (window.userData.guid === '') return;

    const result = await axios.get<{ phoneNumber: string; email: string } | null>(
      `${apiPathPrefix}/preorder/preOrderRecord/userInfo`
    );

    parentData.loginUserInfo = result.data;
  } catch (err) {
    console.log('유저 데이터 겟 실패');
  }
};

onBeforeMount(async () => {
  await getData();
  setParentData(parentData);
});
// window resize
const deviceInfoStore = useDeviceInfoStore();

const device = computed(() => deviceInfoStore.$state.device);
const previewPane = ref<any>();
const layoutChecked = ref(false);
const screen = reactive<{ width: number; height: number | null }>(
  { width: 900, height: null } //height 표시할까? 말까?
);

const dataasdf = ref(device);

const bpName = ref<string>('bp-min-1260');
const resize = (): void => {
  const cW: number = window.innerWidth;
  screen.width = cW;
  if (cW < 960) {
    if (device.value == 'mobile') return;
    bpName.value = 'bp-max-960';
    deviceInfoStore.setDevice('mobile');
  } else {
    if (cW < 1260) {
      bpName.value = 'bp-max-1260';
    } else {
      bpName.value = 'bp-max';
    }
    if (device.value == 'pc') return;
    deviceInfoStore.setDevice('pc');
  }
};

window.addEventListener('resize', resize);
provide('screen', screen);

window.addEventListener('resize', resize);
resize();
onMounted(() => {
  resize();
  nextTick(() => {
    resize();
    setTimeout(resize, 50);
  });
});
onBeforeUnmount(() => {
  window.removeEventListener('resize', resize);
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    "data-device": device.value,
    "data-bp": bpName.value,
    style: _normalizeStyle([{"background-color":"#f2f2f2"}, parentColor])
  }, [
    _createVNode(Nested, {
      list: data.value,
      parentData: parentData
    }, null, 8, ["list", "parentData"]),
    _hoisted_2
  ], 12, _hoisted_1))
}
}

})